import React, { useState, useEffect } from "react";
import SEO from "../seo";
const DeactivateYourStore = () => {
  const [activeSection, setActiveSection] = useState("overview");

  useEffect(() => {
    const handleScroll = () => {
      const sections = [
        "overview",
        "deactivation-steps",
        "after-deactivation",
        "reactivation",
        "store-closure-checklist",
        "domain-rule",
        "payments-billing",
        "orders-fulfillment",
        "marketing-channels",
        "pause-store",
      ];

      const scrollPosition = window.scrollY + 100;

      for (const sectionId of sections) {
        const element = document.getElementById(sectionId);
        if (element) {
          const offsetTop = element.offsetTop;
          const offsetHeight = element.offsetHeight;

          if (
            scrollPosition >= offsetTop &&
            scrollPosition < offsetTop + offsetHeight
          ) {
            setActiveSection(sectionId);
            break;
          }
        }
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <div className="flex flex-col lg:flex-row gap-6">
      <SEO
        title="Deactivate Your Store | xPage Help Center"
        description="Learn how to deactivate your xPage store, follow the step-by-step guide, understand the effects after deactivation, reactivation process, and closure checklist."
        keywords="xPage, deactivate store, store closure, pause store, billing, payments, reactivation, fulfillment, marketing channels"
        canonical="https://help.xpage.ai/help/landing-page-account/Deactivate%20your%20store"
        type="article"
      />
      {/* Main Content */}
      <div className="flex-1 prose max-w-none">
        {/* Header Section */}
        <header className="mb-6">
          <h1 className="text-systemTitle text-2xl font-semibold mb-3">
            Deactivate your store
          </h1>
          <p className="text-sm text-systemLabel">
            As the store owner, if your store meets the required conditions, you
            can cancel your subscription and deactivate your store at any time
            via the xPage admin.
          </p>
        </header>

        {/* Overview Section */}
        <section id="overview" className="mb-6 scroll-mt-20">
          <h2 className="text-sm font-semibold text-systemTitle mb-1">
            Overview
          </h2>

          <div className="bg-systemBackground border border-systemBorder rounded-lg p-4 mt-2">
            <p className="text-sm text-systemLabel mb-4">
              Before deactivating, please read and understand the xPage Terms of
              Service so you are aware of the impact on your account, data, and
              domain.
            </p>

            <div className="bg-system200 border-l-4 border-system400 px-4 py-3 mb-4 rounded-r-md">
              <p className="text-sm text-systemLabel">
                <span className="font-semibold">TIP</span>
                <br />
                Stores on a free trial cannot be manually deactivated. Your
                trial store will be automatically deactivated when the trial
                period ends.
              </p>
            </div>

            <h3 className="text-sm font-semibold text-systemTitle mb-2">
              Cancel your subscription and deactivate your store
            </h3>

            <div className="bg-system200 border-l-4 border-system400 px-4 py-3 rounded-r-md">
              <p className="text-sm text-systemLabel">
                <span className="font-semibold">TIP</span>
                <br />
                Deactivating your store will immediately cancel your
                subscription, and you will lose access to the xPage admin. To
                reactivate the store later, you will need to re-subscribe to a
                paid plan.
              </p>
            </div>
          </div>
        </section>

        {/* Deactivation Steps Section */}
        <section id="deactivation-steps" className="mb-6 scroll-mt-20">
          <h2 className="text-sm font-semibold text-systemTitle mb-1">
            Steps to deactivate your store:
          </h2>

          <div className="bg-systemBackground border border-systemBorder rounded-lg p-4 mt-2">
            <ol className="list-decimal list-inside space-y-1 text-sm text-systemLabel ml-4">
              <li className="mb-2">
                <span className="font-medium">Log in to the xPage admin.</span>
              </li>
              <li className="mb-2">
                <span className="font-medium">
                  Go to Settings then Billing & Subscription.
                </span>
              </li>
              <li className="mb-2">
                <span className="font-medium">
                  In the Subscription section, click Deactivate Store.
                </span>
              </li>
              <li className="mb-2">
                <span className="font-medium">
                  Select a reason for closing and optionally add comments.
                </span>
              </li>
              <li className="mb-2">
                <span className="font-medium">
                  Click Continue to confirm the deactivation.
                </span>
              </li>
            </ol>
          </div>
        </section>

        {/* After Deactivation Section */}
        <section id="after-deactivation" className="mb-6 scroll-mt-20">
          <h2 className="text-sm font-semibold text-systemTitle mb-1">
            After deactivation:
          </h2>

          <div className="bg-systemBackground border border-systemBorder rounded-lg p-4 mt-2">
            <ul className="list-disc list-inside space-y-1 text-sm text-systemLabel ml-4">
              <li>You can still access and download past invoices.</li>
              <li>
                Your store data (including products, sales, and customer
                information will be retained for two years, allowing you to
                reactivate at any time before deletion)
              </li>
            </ul>
          </div>
        </section>

        {/* Reactivation Section */}
        <section id="reactivation" className="mb-6 scroll-mt-20">
          <h2 className="text-sm font-semibold text-systemTitle mb-1">
            Reactivate your store
          </h2>

          <div className="bg-systemBackground border border-systemBorder rounded-lg p-4 mt-2">
            <p className="text-sm text-systemLabel mb-4">
              If you decide to reopen your store, you can log in at any time and
              choose a plan to reactivate.
            </p>

            <h3 className="text-sm font-semibold text-systemTitle mb-2">
              Steps to reactivate:
            </h3>
            <ol className="list-decimal list-inside space-y-1 text-sm text-systemLabel ml-4">
              <li className="mb-2">
                <span className="font-medium">
                  Log in to the xPage admin and select your deactivated store.
                </span>
              </li>
              <li className="mb-2">
                <span className="font-medium">
                  Click Continue Subscription or Change Plan.
                </span>
              </li>
              <li className="mb-2">
                <span className="font-medium">
                  Complete the payment to reactivate your store.
                </span>
              </li>
            </ol>
          </div>
        </section>

        {/* Store Closure Checklist Section */}
        <section id="store-closure-checklist" className="mb-6 scroll-mt-20">
          <h2 className="text-sm font-semibold text-systemTitle mb-1">
            Store closure checklist
          </h2>

          <div className="bg-systemBackground border border-systemBorder rounded-lg p-4 mt-2">
            <h3 className="text-sm font-semibold text-systemTitle mb-3">
              Account access and visibility
            </h3>
            <table className="w-full mb-6">
              <tbody className="border divide-y divide-systemBorder">
                <tr className="bg-gray-400/10 transition-colors">
                  <td className="p-3 align-top w-1/3 border-r border-systemBorder">
                    <strong className="text-systemTitle text-sm">Item</strong>
                  </td>
                  <td className="p-3 align-top text-systemLabel text-sm">
                    Description
                  </td>
                </tr>
                <tr className="">
                  <td className="p-3 align-top w-1/3 border-r border-systemBorder">
                    <strong className="text-systemTitle text-sm">
                      Admin access
                    </strong>
                  </td>
                  <td className="p-3 align-top text-systemLabel text-sm">
                    Store will be marked as Inactive; admin will no longer be
                    accessible.
                  </td>
                </tr>
                <tr className="bg-gray-400/10 transition-colors">
                  <td className="p-3 align-top w-1/3 border-r border-systemBorder">
                    <strong className="text-systemTitle text-sm">
                      Linked account
                    </strong>
                  </td>
                  <td className="p-3 align-top text-systemLabel text-sm">
                    Your xPage account remains active and can be used to
                    reactivate the store.
                  </td>
                </tr>
                <tr className="">
                  <td className="p-3 align-top w-1/3 border-r border-systemBorder">
                    <strong className="text-systemTitle text-sm">
                      Data retention
                    </strong>
                  </td>
                  <td className="p-3 align-top text-systemLabel text-sm">
                    Store data is retained for 2 years; automatically deleted
                    afterward.
                  </td>
                </tr>
                <tr className="bg-gray-400/10 transition-colors">
                  <td className="p-3 align-top w-1/3 border-r border-systemBorder">
                    <strong className="text-systemTitle text-sm">
                      Storefront view
                    </strong>
                  </td>
                  <td className="p-3 align-top text-systemLabel text-sm">
                    Visitors will see a Store Closed message. Consider linking
                    to a new store or contact info.
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </section>

        {/* Domain Rule Section */}
        <section id="domain-rule" className="mb-6 scroll-mt-20">
          <h2 className="text-sm font-semibold text-systemTitle mb-1">
            Unique domain rule:
          </h2>

          <div className="bg-systemBackground border border-systemBorder rounded-lg p-4 mt-2">
            <p className="text-sm text-systemLabel mb-4">
              When you create a store, xPage assigns a default xxx.xpage.ai
              domain. You can customize it once. Once the store is closed,
              whether default or customized, that domain cannot be reused.
            </p>

            <div className="bg-system200 rounded-lg p-3">
              <p className="text-sm text-systemLabel">
                Example: If you close a store with the domain
                very-good-things.xpage.ai, you can still use the brand name
                again, but you cannot use the same domain.
              </p>
            </div>
          </div>
        </section>

        {/* Payments and Billing Section */}
        <section id="payments-billing" className="mb-6 scroll-mt-20">
          <h2 className="text-sm font-semibold text-systemTitle mb-1">
            Payments and billing
          </h2>

          <div className="bg-systemBackground border border-systemBorder rounded-lg p-4 mt-2">
            <h3 className="text-sm font-semibold text-systemTitle mb-3">
              For xPage Payments users
            </h3>
            <table className="w-full mb-6">
              <tbody className="border divide-y divide-systemBorder">
                <tr className="bg-gray-400/10 transition-colors">
                  <td className="p-3 align-top w-1/3 border-r border-systemBorder">
                    <strong className="text-systemTitle text-sm">Item</strong>
                  </td>
                  <td className="p-3 align-top text-systemLabel text-sm">
                    Restriction
                  </td>
                </tr>
                <tr className="">
                  <td className="p-3 align-top w-1/3 border-r border-systemBorder">
                    <strong className="text-systemTitle text-sm">
                      New payments
                    </strong>
                  </td>
                  <td className="p-3 align-top text-systemLabel text-sm">
                    Customers cannot make payments after the store is closed.
                  </td>
                </tr>
                <tr className="bg-gray-400/10 transition-colors">
                  <td className="p-3 align-top w-1/3 border-r border-systemBorder">
                    <strong className="text-systemTitle text-sm">
                      Payouts
                    </strong>
                  </td>
                  <td className="p-3 align-top text-systemLabel text-sm">
                    Pending settlement funds will be processed normally
                    according to your payout schedule.
                  </td>
                </tr>
                <tr className="">
                  <td className="p-3 align-top w-1/3 border-r border-systemBorder">
                    <strong className="text-systemTitle text-sm">
                      Refunds
                    </strong>
                  </td>
                  <td className="p-3 align-top text-systemLabel text-sm">
                    Refunds and manual captures cannot be processed from the
                    admin.
                  </td>
                </tr>
                <tr className="bg-gray-400/10 transition-colors">
                  <td className="p-3 align-top w-1/3 border-r border-systemBorder">
                    <strong className="text-systemTitle text-sm">
                      Chargebacks
                    </strong>
                  </td>
                  <td className="p-3 align-top text-systemLabel text-sm">
                    You cannot dispute chargebacks; all disputes will be
                    automatically accepted.
                  </td>
                </tr>
              </tbody>
            </table>

            <h3 className="text-sm font-semibold text-systemTitle mb-3">
              For third-party payment gateway users
            </h3>
            <table className="w-full mb-6">
              <tbody className="border divide-y divide-systemBorder">
                <tr className="bg-gray-400/10 transition-colors">
                  <td className="p-3 align-top w-1/3 border-r border-systemBorder">
                    <strong className="text-systemTitle text-sm">Item</strong>
                  </td>
                  <td className="p-3 align-top text-systemLabel text-sm">
                    Restriction
                  </td>
                </tr>
                <tr className="">
                  <td className="p-3 align-top w-1/3 border-r border-systemBorder">
                    <strong className="text-systemTitle text-sm">
                      New payments
                    </strong>
                  </td>
                  <td className="p-3 align-top text-systemLabel text-sm">
                    Payments cannot be initiated once the store is closed.
                  </td>
                </tr>
                <tr className="bg-gray-400/10 transition-colors">
                  <td className="p-3 align-top w-1/3 border-r border-systemBorder">
                    <strong className="text-systemTitle text-sm">
                      Refunds and disputes
                    </strong>
                  </td>
                  <td className="p-3 align-top text-systemLabel text-sm">
                    Must be handled directly with the third-party provider.
                  </td>
                </tr>
              </tbody>
            </table>

            <h3 className="text-sm font-semibold text-systemTitle mb-3">
              For manual payment method users
            </h3>
            <table className="w-full mb-6">
              <tbody className="border divide-y divide-systemBorder">
                <tr className="bg-gray-400/10 transition-colors">
                  <td className="p-3 align-top w-1/3 border-r border-systemBorder">
                    <strong className="text-systemTitle text-sm">Item</strong>
                  </td>
                  <td className="p-3 align-top text-systemLabel text-sm">
                    Restriction
                  </td>
                </tr>
                <tr className="">
                  <td className="p-3 align-top w-1/3 border-r border-systemBorder">
                    <strong className="text-systemTitle text-sm">
                      New orders
                    </strong>
                  </td>
                  <td className="p-3 align-top text-systemLabel text-sm">
                    Manual payment methods are disabled after closure.
                  </td>
                </tr>
                <tr className="bg-gray-400/10 transition-colors">
                  <td className="p-3 align-top w-1/3 border-r border-systemBorder">
                    <strong className="text-systemTitle text-sm">
                      Post-sale
                    </strong>
                  </td>
                  <td className="p-3 align-top text-systemLabel text-sm">
                    Refund and return processes cannot be performed.
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </section>

        {/* Orders and Fulfillment Section */}
        <section id="orders-fulfillment" className="mb-6 scroll-mt-20">
          <h2 className="text-sm font-semibold text-systemTitle mb-1">
            Orders and fulfillment
          </h2>

          <div className="bg-systemBackground border border-systemBorder rounded-lg p-4 mt-2">
            <table className="w-full mb-6">
              <tbody className="border divide-y divide-systemBorder">
                <tr className="bg-gray-400/10 transition-colors">
                  <td className="p-3 align-top w-1/3 border-r border-systemBorder">
                    <strong className="text-systemTitle text-sm">Status</strong>
                  </td>
                  <td className="p-3 align-top text-systemLabel text-sm">
                    Restriction
                  </td>
                </tr>
                <tr className="">
                  <td className="p-3 align-top w-1/3 border-r border-systemBorder">
                    <strong className="text-systemTitle text-sm">
                      Incomplete orders
                    </strong>
                  </td>
                  <td className="p-3 align-top text-systemLabel text-sm">
                    Cannot proceed with payment, fulfillment, or export.
                  </td>
                </tr>
                <tr className="bg-gray-400/10 transition-colors">
                  <td className="p-3 align-top w-1/3 border-r border-systemBorder">
                    <strong className="text-systemTitle text-sm">
                      After-sales flows
                    </strong>
                  </td>
                  <td className="p-3 align-top text-systemLabel text-sm">
                    Refund, return, and exchange processes will be disabled.
                  </td>
                </tr>
                <tr className="">
                  <td className="p-3 align-top w-1/3 border-r border-systemBorder">
                    <strong className="text-systemTitle text-sm">
                      Purchased labels only
                    </strong>
                  </td>
                  <td className="p-3 align-top text-systemLabel text-sm">
                    Ship or void labels promptly to avoid waste.
                  </td>
                </tr>
                <tr className="bg-gray-400/10 transition-colors">
                  <td className="p-3 align-top w-1/3 border-r border-systemBorder">
                    <strong className="text-systemTitle text-sm">
                      Shipped orders
                    </strong>
                  </td>
                  <td className="p-3 align-top text-systemLabel text-sm">
                    Tracking updates continue, but after-sales support ends.
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </section>

        {/* Marketing and Sales Channels Section */}
        <section id="marketing-channels" className="mb-6 scroll-mt-20">
          <h2 className="text-sm font-semibold text-systemTitle mb-1">
            Marketing and sales channels
          </h2>

          <div className="bg-systemBackground border border-systemBorder rounded-lg p-4 mt-2">
            <table className="w-full mb-6">
              <tbody className="border divide-y divide-systemBorder">
                <tr className="bg-gray-400/10 transition-colors">
                  <td className="p-3 align-top w-1/3 border-r border-systemBorder">
                    <strong className="text-systemTitle text-sm">Item</strong>
                  </td>
                  <td className="p-3 align-top text-systemLabel text-sm">
                    Status
                  </td>
                </tr>
                <tr className="">
                  <td className="p-3 align-top w-1/3 border-r border-systemBorder">
                    <strong className="text-systemTitle text-sm">
                      Email campaigns
                    </strong>
                  </td>
                  <td className="p-3 align-top text-systemLabel text-sm">
                    Emails not yet in the sending queue are paused and saved as
                    drafts; emails already sending will complete.
                  </td>
                </tr>
                <tr className="bg-gray-400/10 transition-colors">
                  <td className="p-3 align-top w-1/3 border-r border-systemBorder">
                    <strong className="text-systemTitle text-sm">
                      Automation flows
                    </strong>
                  </td>
                  <td className="p-3 align-top text-systemLabel text-sm">
                    All flows will be paused.
                  </td>
                </tr>
                <tr className="">
                  <td className="p-3 align-top w-1/3 border-r border-systemBorder">
                    <strong className="text-systemTitle text-sm">
                      Scheduled posts
                    </strong>
                  </td>
                  <td className="p-3 align-top text-systemLabel text-sm">
                    All scheduled posts will be canceled and saved as drafts.
                  </td>
                </tr>
                <tr className="bg-gray-400/10 transition-colors">
                  <td className="p-3 align-top w-1/3 border-r border-systemBorder">
                    <strong className="text-systemTitle text-sm">Popups</strong>
                  </td>
                  <td className="p-3 align-top text-systemLabel text-sm">
                    All active popups will be taken offline.
                  </td>
                </tr>
                <tr className="">
                  <td className="p-3 align-top w-1/3 border-r border-systemBorder">
                    <strong className="text-systemTitle text-sm">
                      SEO functions
                    </strong>
                  </td>
                  <td className="p-3 align-top text-systemLabel text-sm">
                    SEO scans and robots.txt rules will be disabled.
                  </td>
                </tr>
                <tr className="bg-gray-400/10 transition-colors">
                  <td className="p-3 align-top w-1/3 border-r border-systemBorder">
                    <strong className="text-systemTitle text-sm">
                      Channel sync
                    </strong>
                  </td>
                  <td className="p-3 align-top text-systemLabel text-sm">
                    All sales channel authorizations will be disconnected;
                    product sync will stop.
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </section>

        {/* Pause Store Section */}
        <section id="pause-store" className="scroll-mt-20">
          <h2 className="text-sm font-semibold text-systemTitle mb-1">
            Pause your store
          </h2>

          <div className="bg-systemBackground border border-systemBorder rounded-lg p-4 mt-2">
            <p className="text-sm text-systemLabel">
              Pausing your xPage store temporarily takes it offline without
              canceling your subscription or losing access to the admin. This is
              useful for activities such as inventory updates, seasonal breaks,
              or rebranding. While paused, customers cannot place new orders and
              your storefront will display a "Store Temporarily Closed" message,
              but you can still access the admin to manage products, fulfill
              existing orders, and process refunds. Marketing campaigns and
              automation flows are paused until you unpause the store. To pause,
              log in to the xPage admin, go to Settings then Store Status, click
              Pause Store, and confirm. To resume, return to the same page and
              select Resume Store; your storefront will be live immediately.
            </p>
          </div>
        </section>
      </div>

      {/* Table of Contents Sidebar */}
      <div className="lg:w-64 flex-shrink-0 lg:block hidden">
        <div className="sticky top-6 bg-systemBackground border border-systemBorder rounded-lg p-4">
          <h3 className="text-systemTitle text-sm font-semibold mb-3">
            On this page
          </h3>
          <ul className="text-systemLabel text-sm space-y-2">
            <li>
              <a
                href="#overview"
                className={`block py-1 transition-colors border-l-2 pl-3 ${
                  activeSection === "overview"
                    ? "text-deepPurple font-semibold border-deepPurple"
                    : "border-transparent hover:text-deepPurple"
                }`}
              >
                Overview
              </a>
            </li>
            <li>
              <a
                href="#deactivation-steps"
                className={`block py-1 transition-colors border-l-2 pl-3 ${
                  activeSection === "deactivation-steps"
                    ? "text-deepPurple font-semibold border-deepPurple"
                    : "border-transparent hover:text-deepPurple"
                }`}
              >
                Deactivation Steps
              </a>
            </li>
            <li>
              <a
                href="#after-deactivation"
                className={`block py-1 transition-colors border-l-2 pl-3 ${
                  activeSection === "after-deactivation"
                    ? "text-deepPurple font-semibold border-deepPurple"
                    : "border-transparent hover:text-deepPurple"
                }`}
              >
                After Deactivation
              </a>
            </li>
            <li>
              <a
                href="#reactivation"
                className={`block py-1 transition-colors border-l-2 pl-3 ${
                  activeSection === "reactivation"
                    ? "text-deepPurple font-semibold border-deepPurple"
                    : "border-transparent hover:text-deepPurple"
                }`}
              >
                Reactivation
              </a>
            </li>
            <li>
              <a
                href="#store-closure-checklist"
                className={`block py-1 transition-colors border-l-2 pl-3 ${
                  activeSection === "store-closure-checklist"
                    ? "text-deepPurple font-semibold border-deepPurple"
                    : "border-transparent hover:text-deepPurple"
                }`}
              >
                Store Closure Checklist
              </a>
            </li>
            <li>
              <a
                href="#domain-rule"
                className={`block py-1 transition-colors border-l-2 pl-3 ${
                  activeSection === "domain-rule"
                    ? "text-deepPurple font-semibold border-deepPurple"
                    : "border-transparent hover:text-deepPurple"
                }`}
              >
                Domain Rule
              </a>
            </li>
            <li>
              <a
                href="#payments-billing"
                className={`block py-1 transition-colors border-l-2 pl-3 ${
                  activeSection === "payments-billing"
                    ? "text-deepPurple font-semibold border-deepPurple"
                    : "border-transparent hover:text-deepPurple"
                }`}
              >
                Payments & Billing
              </a>
            </li>
            <li>
              <a
                href="#orders-fulfillment"
                className={`block py-1 transition-colors border-l-2 pl-3 ${
                  activeSection === "orders-fulfillment"
                    ? "text-deepPurple font-semibold border-deepPurple"
                    : "border-transparent hover:text-deepPurple"
                }`}
              >
                Orders & Fulfillment
              </a>
            </li>
            <li>
              <a
                href="#marketing-channels"
                className={`block py-1 transition-colors border-l-2 pl-3 ${
                  activeSection === "marketing-channels"
                    ? "text-deepPurple font-semibold border-deepPurple"
                    : "border-transparent hover:text-deepPurple"
                }`}
              >
                Marketing & Channels
              </a>
            </li>
            <li>
              <a
                href="#pause-store"
                className={`block py-1 transition-colors border-l-2 pl-3 ${
                  activeSection === "pause-store"
                    ? "text-deepPurple font-semibold border-deepPurple"
                    : "border-transparent hover:text-deepPurple"
                }`}
              >
                Pause Store
              </a>
            </li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default DeactivateYourStore;
